import { UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { IContact, ImpersonationBroker, asApiError } from '../../../extViewmodels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { queryKeys } from './queryKeys';

export type UpdateContactVariables = {
	companyId: string;
	contact: IContact;
};

/**
 * Update an existing contact via `PUT lead/{companyId}/contact/{contactId}` —
 * the same path used by `AidaContactViewModel` (which overrides
 * `getEntityApiPath` to `lead/{companyId}/contact`). The PUT body is the full
 * IContact entity; the backend rejects partial updates.
 *
 * Mirrors the legacy `expand=HouseholdMembers&allowDuplicateEmail=true` query
 * params used by `EntityViewModel.mUpdate` for contacts.
 */
export function useUpdateContact(): UseMutationResult<IContact, Error, UpdateContactVariables> {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation<IContact, Error, UpdateContactVariables>({
		mutationFn: async ({ companyId, contact }) => {
			if (!contact.id) {
				throw new Error('Cannot update a contact without an id');
			}
			const result = await userSession.webServiceHelper.callWebServiceAsync<IContact>(
				ImpersonationBroker.composeApiUrl({
					urlPath: `lead/${companyId}/contact/${contact.id}`,
					queryParams: { expand: 'HouseholdMembers', allowDuplicateEmail: true },
				}),
				'PUT',
				contact
			);
			if (!result.success) {
				throw asApiError(result);
			}
			return result.value;
		},
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: queryKeys.focusedView.contacts(variables.companyId) });
		},
	});
}
